import { useTranslation } from "react-i18next";
import { ExternalLink, ShoppingBag, Star } from "lucide-react";
import { useImageErrorHandling } from "@/lib/image-utils";
import { formatPrice } from "@/features/orders/utils/format-price";
import type { WebSearchProduct } from "@/api/models/api-types";

interface WebProductCardProps {
  product: WebSearchProduct;
}

export function WebProductCard({ product }: WebProductCardProps) {
  const { t } = useTranslation();
  const { handleImageError, isImageFailed } = useImageErrorHandling();

  const productName = product.title || t("instantSearch.product");
  const productPrice =
    product.price === null
      ? t("chat.webProducts.priceUnavailable")
      : formatPrice(product.price, product.currency || "TRY");
  const productImage = product.imageUrl || "";
  const productUrl = product.productUrl;
  const sourceName = product.source;
  const hasRating =
    typeof product.rating === "number" && !Number.isNaN(product.rating);

  return (
    <a
      href={productUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="@container/card flex h-full w-full flex-col border border-gray-200 dark:border-gray-700 rounded-xl @min-[200px]/card:rounded-2xl p-1.5 @min-[200px]/card:p-2 group cursor-pointer hover:shadow-lg transition-shadow bg-white dark:bg-gray-800"
    >
      <div className="relative aspect-square shrink-0 rounded-lg @min-[200px]/card:rounded-xl overflow-hidden mb-1.5 @min-[200px]/card:mb-3 bg-gray-100 dark:bg-gray-800">
        {productImage && !isImageFailed(productImage) ? (
          <img
            src={productImage}
            alt={productName}
            // Google Shopping (encrypted-tbn*.gstatic.com) Referer ile hotlink'i engeller
            referrerPolicy="no-referrer"
            loading="lazy"
            onError={() => handleImageError(productImage)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ShoppingBag className="h-8 w-8 @min-[200px]/card:h-12 @min-[200px]/card:w-12" />
          </div>
        )}

        <div className="absolute top-1.5 right-1.5 @min-[200px]/card:top-2 @min-[200px]/card:right-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-1 py-0.5 @min-[200px]/card:px-1.5 @min-[200px]/card:py-0.5 rounded-md flex items-center gap-0.5 @min-[200px]/card:gap-1 opacity-0 @min-[200px]/card:group-hover:opacity-100 transition-opacity">
          <ExternalLink className="w-3 h-3 @min-[200px]/card:w-3.5 @min-[200px]/card:h-3.5 text-gray-700 dark:text-gray-300" />
          <span className="text-[10px] @min-[200px]/card:text-xs font-medium text-gray-700 dark:text-gray-300 hidden @min-[200px]/card:inline">
            {t("chat.webProducts.viewOnSeller")}
          </span>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-1 @min-[200px]/card:gap-2 px-0.5 @min-[200px]/card:px-1">
        <div className="flex min-h-6 shrink-0 items-center justify-between gap-1 @min-[200px]/card:min-h-7 @min-[200px]/card:gap-2">
          {sourceName && (
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-1 @min-[200px]/card:px-1.5 py-0.5 rounded-md flex items-center gap-0.5 @min-[200px]/card:gap-1 max-w-[70%] flex-shrink-0">
              <span className="text-[10px] @min-[200px]/card:text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                {sourceName}
              </span>
              <ExternalLink className="w-2.5 h-2.5 @min-[200px]/card:w-3 @min-[200px]/card:h-3 text-gray-700 dark:text-gray-300 flex-shrink-0" />
            </div>
          )}

          {hasRating && (
            <div className="flex items-center gap-0.5 text-[10px] @min-[200px]/card:text-xs font-medium text-amber-600 dark:text-amber-400 flex-shrink-0">
              <Star className="w-3 h-3 fill-current" />
              <span>{product.rating!.toFixed(1)}</span>
              {typeof product.ratingCount === "number" &&
                product.ratingCount > 0 && (
                  <span className="text-gray-500 dark:text-gray-400">
                    ({product.ratingCount})
                  </span>
                )}
            </div>
          )}
        </div>

        <p className="text-xs @min-[200px]/card:text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2 min-h-[1.75rem] @min-[200px]/card:min-h-[2.5rem] leading-tight">
          {productName}
        </p>

        <div className="mt-auto flex min-h-5 items-center gap-1 overflow-hidden @min-[200px]/card:min-h-6 @min-[200px]/card:gap-2">
          <span className="text-xs @min-[200px]/card:text-sm font-semibold text-primary-500 dark:text-primary-400">
            {productPrice}
          </span>
        </div>
      </div>
    </a>
  );
}
